import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Button } from 'ui-components/components/button';
import { Drawer } from 'ui-components/components/drawer/drawer';
import { DrawerHeader } from 'ui-components/components/drawer/drawer-header';
import { FlexColumn } from 'ui-components/components/flex';
import { Gap } from 'ui-components/components/flex/gap';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Text } from 'ui-components/components/text/text';
import { TextType } from 'ui-components/components/text/text-type';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { HtmlEditorContent } from './components/html-editor-content/html-editor-content';
import { getTemplateConfig } from './html-template.api';
import { messages } from './messages';
import { useGetHtmlTemplatePreview } from './preview/use-get-html-template-preview';
import style from './styles.module.less';
import { Layout, Orientation, PreviewTrigger } from './types';
import Global from '../../store/global-selectors';
import { Workflows } from '../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../tracking/use-workflow-tracking';

import type { Orientation as OrientationType, Layout as LayoutType, TemplateType, HtmlTemplateId } from './types';
import type { ReactNode } from 'react';

type HtmlEditorDrawerProps = {
  templateKey?: HtmlTemplateId;
  templateType: TemplateType;
  initialValue?: string;
  initialTemplateName?: string;
  initialLayout?: LayoutType;
  initialOrientation?: OrientationType;
  isLoadingContent?: boolean;
  additionalFormFields?: ReactNode;
  allowedLayouts?: LayoutType[];
  source?: string;
  sourceIds?: number[] | null;
  previewToolbar?: ReactNode;

  title: ReactNode;
  onCancel: () => void;
  onSave: (data: { name: string; html: string; layout: LayoutType; orientation: OrientationType }) => Promise<unknown>;
  onSuccessfulSave: () => void;
};

export const HtmlEditorDrawer = ({
  templateKey,
  templateType,
  initialValue = '',
  initialTemplateName = '',
  initialLayout = Layout.EightAndHalfByEleven,
  initialOrientation = Orientation.Portrait,
  isLoadingContent = false,
  additionalFormFields,
  allowedLayouts,
  source,
  sourceIds = null,
  previewToolbar,

  title,
  onCancel,
  onSave,
  onSuccessfulSave,
}: HtmlEditorDrawerProps) => {
  const sellerId = useSelector(Global.selectSellerSellerId);
  const trackWorkflow = useTrackWorkflow(Workflows.HTML_EDITOR_FLOW);

  const {
    data: config,
    isLoading: isLoadingConfig,
    isError: hasLoadingError,
    refetch: refetchConfig,
  } = useQuery({
    queryKey: ['html-template-config', templateType],
    queryFn: () => getTemplateConfig(templateType),
    staleTime: Infinity,
  });

  const {
    previewUrl: initialPreviewUrl,
    isLoading: isLoadingInitialPreview,
    refetch: fetchInitialPreview,
    fetchError,
  } = useGetHtmlTemplatePreview(templateType, null, null, trackWorkflow);

  const initialSourceIds = useRef(sourceIds);

  useEffect(() => {
    if (initialValue) {
      fetchInitialPreview(initialValue, initialSourceIds.current, initialLayout, initialOrientation, PreviewTrigger.Automatic);
    }
  }, [initialValue, initialLayout, initialOrientation, fetchInitialPreview]);

  const isWaitingForPreview = !!initialValue && !initialPreviewUrl && !isLoadingInitialPreview && !fetchError;
  const isInitializing = isLoadingConfig || isLoadingInitialPreview || isLoadingContent || isWaitingForPreview;
  const isStartEventSent = useRef(false);

  useEffect(() => {
    if (isStartEventSent.current) {
      return;
    }

    trackWorkflow(WorkflowSteps.START, {
      sellerId,
      templateKey,
      templateType,
      source,
      mode: templateKey ? 'edit' : 'create',
    });
    isStartEventSent.current = true;
  }, [trackWorkflow, templateKey, templateType, sellerId, source]);

  return (
    <Drawer onCancel={onCancel} onClose={onCancel}>
      <DrawerHeader closeButtonAriaLabel={messages.closeDrawer} title={title} />

      {isInitializing ? (
        <FlexColumn className={style['state-container']}>
          <LoadingIndicator />
        </FlexColumn>
      ) : null}

      {!isInitializing && hasLoadingError ? (
        <FlexColumn className={style['state-container']} gap={Gap.Space16}>
          <Text textType={TextType.Description} value={messages.loadEditorError} />
          <Button onClick={() => refetchConfig()} theme={THEME_CONSTANTS.primary}>
            <FormattedMessage {...messages.retryButton} />
          </Button>
        </FlexColumn>
      ) : null}

      {!hasLoadingError && !isInitializing && config ? (
        <HtmlEditorContent
          additionalFormFields={additionalFormFields}
          allowedLayouts={allowedLayouts}
          hasInitialFetchError={fetchError}
          initialLayout={initialLayout}
          initialOrientation={initialOrientation}
          initialPreviewUrl={initialPreviewUrl}
          initialTemplateName={initialTemplateName}
          initialValue={initialValue}
          onCancel={onCancel}
          onSave={onSave}
          onSuccessfulSave={onSuccessfulSave}
          previewToolbar={previewToolbar}
          schema={config.schema}
          sourceIds={sourceIds}
          templateType={templateType}
          tracking={trackWorkflow}
        />
      ) : null}
    </Drawer>
  );
};
